import type { SlideData } from '@digital-twins/deck-ui/slides/contracts'
import { FailedExhibit } from '@/features/studies/report/reportViewState'

/**
 * Who sees a failed exhibit as a placeholder slide in the deck pager. One
 * switch: signed-in report viewers for now (owner QA), never a public share
 * link; later Fairgen staff only, or nobody (log only).
 */
export type FailedExhibitAudience = 'signed_in_viewers' | 'nobody'

export const FAILED_EXHIBIT_AUDIENCE: FailedExhibitAudience = 'signed_in_viewers'

/** Who is looking at the deck. */
export interface FailedExhibitViewer {
  /** A public share-link view: never shown failure details. */
  isPublicView: boolean
}

/** The failed exhibits a viewer is shown under the audience switch. */
export function visibleFailedExhibits(
  failedExhibits: Array<FailedExhibit>,
  viewer: FailedExhibitViewer,
  audience: FailedExhibitAudience = FAILED_EXHIBIT_AUDIENCE,
): Array<FailedExhibit> {
  switch (audience) {
    case 'signed_in_viewers':
      return viewer.isPublicView ? [] : failedExhibits
    case 'nobody':
      return []
    default: {
      const unhandled: never = audience
      return unhandled
    }
  }
}

/** One stop in the deck pager: a rendered slide or a failed exhibit's placeholder. */
export type DeckPagerEntry =
  | { kind: 'slide'; slide: SlideData }
  | { kind: 'failed_exhibit'; exhibit: FailedExhibit }

/** The stable id a deck slide carries (`slideId`, else `id`). */
export function deckSlideId(slide: SlideData): string | null {
  const record = slide as unknown as { slideId?: unknown; id?: unknown }
  if (typeof record.slideId === 'string' && record.slideId) {
    return record.slideId
  }
  if (typeof record.id === 'string' && record.id) {
    return record.id
  }
  return null
}

/** Whether a deck slide was built from a sequence item (`id`, or `id__part_2` and siblings). */
function isSlideOfItem(slideId: string | null, itemId: string): boolean {
  if (!slideId) {
    return false
  }
  return slideId === itemId || slideId.startsWith(`${itemId}__`)
}

function entryItemMatches(entry: DeckPagerEntry, itemId: string): boolean {
  if (entry.kind === 'failed_exhibit') {
    return entry.exhibit.slideId === itemId
  }
  return isSlideOfItem(deckSlideId(entry.slide), itemId)
}

/**
 * The deck pager: the rendered slides in deck order, with each failed
 * exhibit placed where the accepted sequence (`slideOrder`) puts it, after
 * the last shown slide of the nearest preceding item, or after the first
 * entry (the cover) when no preceding item is shown. A failed exhibit the
 * sequence does not name goes last; one the deck already shows is skipped.
 */
export function buildDeckPagerEntries(
  slides: Array<SlideData>,
  failedExhibits: Array<FailedExhibit>,
  slideOrder: Array<string>,
): Array<DeckPagerEntry> {
  const entries: Array<DeckPagerEntry> = slides.map((slide) => ({ kind: 'slide', slide }))
  if (failedExhibits.length === 0) {
    return entries
  }
  const shownIds = new Set(slides.map(deckSlideId).filter((id): id is string => id !== null))
  const pending = failedExhibits.filter((exhibit) => !shownIds.has(exhibit.slideId))
  const inSequence = pending
    .filter((exhibit) => slideOrder.includes(exhibit.slideId))
    .sort((a, b) => slideOrder.indexOf(a.slideId) - slideOrder.indexOf(b.slideId))
  const unsequenced = pending.filter((exhibit) => !slideOrder.includes(exhibit.slideId))

  for (const exhibit of inSequence) {
    const position = slideOrder.indexOf(exhibit.slideId)
    let insertAt = position === 0 ? 0 : -1
    for (let index = position - 1; index >= 0; index -= 1) {
      const anchorId = slideOrder[index]
      const lastAnchorEntry = findLastIndex(entries, (entry) => entryItemMatches(entry, anchorId))
      if (lastAnchorEntry >= 0) {
        insertAt = lastAnchorEntry + 1
        break
      }
    }
    if (insertAt < 0) {
      // No earlier item is shown: never ahead of the cover.
      insertAt = Math.min(1, entries.length)
      warnUnanchoredOnce(exhibit.slideId)
    }
    entries.splice(insertAt, 0, { kind: 'failed_exhibit', exhibit })
  }
  for (const exhibit of unsequenced) {
    entries.push({ kind: 'failed_exhibit', exhibit })
  }
  return entries
}

/**
 * The pager index a sequence item opens at: its first rendered slide
 * (`id` or `id__part_n`) or its failed-exhibit placeholder; -1 when the
 * pager does not show it (hidden by the owner, or never built).
 */
export function deckPagerIndexOf(entries: Array<DeckPagerEntry>, itemId: string): number {
  return entries.findIndex((entry) => entryItemMatches(entry, itemId))
}

/** The slides a reader pages through: the deck's slides less the ones the owner hid. */
export function readerVisibleSlides(slides: Array<SlideData>, hiddenSlideIndices: Array<number> | undefined): Array<SlideData> {
  const hidden = hiddenSlideIndices ?? []
  return slides.filter((_, index) => !hidden.includes(index))
}

const warnedUnanchored = new Set<string>()

function warnUnanchoredOnce(slideId: string): void {
  if (warnedUnanchored.has(slideId)) {
    return
  }
  warnedUnanchored.add(slideId)
  console.warn(`[deck-pager] no earlier sequence item is shown for failed exhibit ${slideId}; placed after the first slide`)
}

function findLastIndex<T>(items: Array<T>, predicate: (item: T) => boolean): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) {
      return index
    }
  }
  return -1
}
